import React from 'react';
import { AccountProps, PurchaseHistoryItemProps } from '../../state/context';
import { getLocalDate } from 'utils/string';
import { Document, Font, Page, Path, PDFDownloadLink, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';

interface ItemProps {
    item: PurchaseHistoryItemProps;
    account: AccountProps;
}

const PaymentConfirmationItem: React.FC<ItemProps> = ({ item, account }) => {
    if (!item.paidPurchase) {
        return (
            <div className="account-item">
                <div>
                    Du hast <b>{item.title}</b> am <strong> {getLocalDate(item.purchaseDate)}</strong> kostenfrei
                    erworben.
                </div>
            </div>
        );
    }

    return (
        <div className="account-item">
            <div>
                Du hast <b>{item.title}</b> für {item.price}&nbsp;
                {item.currency.symbol} (inkl. 19% Mwst.) am
                <strong> {getLocalDate(item.purchaseDate)}</strong> gekauft.
            </div>
            <PDFDownloadLink
                document={<PrintLayout item={item} account={account} />}
                fileName="gymondo-prevention.pdf"
                className="account-pdf"
            >
                {({ loading, error }) => {
                    if (loading) {
                        return <div className="link link-no-icon">Lädt...</div>;
                    }
                    if (error) {
                        return <div className="link link-no-icon">Fehlgeschlagen</div>;
                    }
                    return <div className="link">Herunterladen</div>;
                }}
            </PDFDownloadLink>
        </div>
    );
};

Font.register({
    family: 'Guillon',
    fonts: [
        {
            src: '//d11y8h2xe0qeoe.cloudfront.net/fonts/guillon-light/guillon-light.ttf',
        },
        {
            src: '//d11y8h2xe0qeoe.cloudfront.net/fonts/guillon-demi/guillon-demi.ttf',
            fontWeight: 'bold',
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        padding: '60 50',
        fontFamily: 'Guillon',
        fontWeight: 'normal',
        color: '#999',
        fontSize: 12,
        lineHeight: 1.5,
    },
    logo: {
        width: 90,
    },
    boldText: {
        fontWeight: 'bold',
    },
    alignRight: {
        textAlign: 'right',
    },
    head: {
        fontSize: 18,
        padding: '15 0',
    },
});

const PrintLayout: React.FC<ItemProps> = ({ item, account }) => {
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={{ marginBottom: 15 }}>
                    <Svg viewBox="0 0 76.562 19.968" style={styles.logo}>
                        <Path
                            d="M31.06 13.9c-2.338 0-4.012-1.7-4.012-4.122s1.768-4.099 4.045-4.099c1.316 0 2.403.595 3.105 1.555l-1.005.96a2.52 2.52 0 0 0-2.122-1.162c-1.388 0-2.473 1.102-2.473 2.746s1.05 2.79 2.495 2.79c.92 0 1.507-.28 1.887-.592v-1.22h-1.916V9.445h3.316v3.093c-.702.812-2.013 1.361-3.32 1.361zm16.915-5.758c-.588 0-1.275.266-1.575.888-.277-.555-.939-.888-1.594-.888-.655 0-1.23.322-1.575.788v-.622h-1.333v5.424h1.4v-2.96c0-.833.397-1.309 1.052-1.309.611 0 .962.431.962 1.142v3.127h1.4V10.65c0-.69.397-1.187 1.052-1.187.589 0 .962.431.962 1.142v3.127h1.4V10.33c0-1.51-1.162-2.188-2.15-2.188zm5.995 0c-1.67 0-2.968 1.186-2.968 2.873 0 1.714 1.31 2.884 2.967 2.884 1.648 0 2.967-1.17 2.967-2.884 0-1.7-1.319-2.873-2.967-2.873zm0 4.569c-.805 0-1.546-.676-1.546-1.696 0-.993.728-1.685 1.545-1.685.823 0 1.546.667 1.546 1.685 0 1.02-.743 1.696-1.546 1.696zm19.625-4.569c-1.67 0-2.967 1.186-2.967 2.873 0 1.714 1.31 2.884 2.967 2.884 1.648 0 2.967-1.17 2.967-2.884 0-1.7-1.32-2.873-2.967-2.873zm0 4.569c-.804 0-1.545-.676-1.545-1.696 0-.993.728-1.685 1.545-1.685.822 0 1.545.667 1.545 1.685 0 1.02-.742 1.696-1.545 1.696zm-12.71-4.569c-.789 0-1.44.392-1.683.913v-.747h-1.333v5.424h1.4v-2.907c0-.89.506-1.395 1.216-1.395.667 0 1.126.453 1.126 1.264v3.038h1.4V10.33c0-1.478-1.059-2.188-2.126-2.188zm7.445.666c-.368-.414-.996-.666-1.684-.666-1.507 0-2.728 1.14-2.728 2.873 0 1.755 1.208 2.884 2.694 2.884.778 0 1.379-.259 1.806-.777v.61h1.31v-7.7H68.33zM66.878 12.7c-.807 0-1.54-.674-1.54-1.684 0-1.006.752-1.651 1.54-1.651.822 0 1.55.674 1.55 1.65 0 1.04-.728 1.685-1.55 1.685zm-26.26.498c0 1.734-1.286 2.834-2.906 2.834-.72 0-1.61-.197-2.216-.595l.605-1.128c.446.273 1.003.47 1.519.47.951 0 1.696-.55 1.696-1.54v-.358c-.293.57-.98.851-1.683.851-1.134 0-2.126-.677-2.126-2.188V8.32h1.4v2.891c0 .811.395 1.233 1.094 1.233.673 0 1.217-.438 1.217-1.426V8.32h1.4v4.94m-26.631 6.708c-1.2 0-1.963-.218-2.828-.581-.865.368-1.594.581-2.743.581C3.662 19.968 0 15.59 0 9.985S3.647 0 8.304 0c1.09 0 2.112.28 2.738.594A6.779 6.779 0 0 1 13.71 0c3.134 0 5.988 2.116 5.988 4.451 0 1.314-.991 2.673-2.65 2.673-1.774 0-2.747-1.339-3.686-2.634-.668-.921-1.35-1.934-2.355-2.552C8.582 3.272 6.88 6.417 6.88 9.985c0 3.644 1.734 6.75 4.263 8.055 2.13-1.115 3.743-3.525 3.743-6.107-.655.714-1.469 1.39-2.685 1.39-1.863 0-2.976-1.485-2.976-2.92 0-1.655 1.347-2.952 3.067-2.952 1.096 0 2.345.633 3.172 2.014.65-.67 1.598-1.245 2.642-1.245 2.474 0 3.324 2.208 3.324 3.599 0 4.347-3.445 8.15-7.443 8.15zm-1.303-1.396a8.43 8.43 0 0 0 1.242.126c3.298 0 6.192-3.231 6.192-6.88 0-.635-.321-2.296-2.012-2.296-.856 0-1.51.52-2.127 1.195.119.344.175.845.175 1.303 0 2.646-1.339 5.086-3.47 6.552zm-4.38-17.3c-3.92 0-6.992 3.843-6.992 8.713 0 4.787 3.156 8.713 7.238 8.713a5.36 5.36 0 0 0 1.08-.122c-2.416-1.734-4.061-4.91-4.061-8.591 0-3.63 1.636-6.792 4-8.543a4.712 4.712 0 0 0-1.265-.17zm4.008 7.491c-1 0-1.775.705-1.775 1.64 0 .622.472 1.608 1.684 1.608.946 0 1.693-.812 2.364-1.582-.49-1.045-1.525-1.666-2.273-1.666zm.098-7.295c.813.688 1.47 1.501 2.015 2.251.941 1.298 1.572 2.094 2.624 2.094.975 0 1.338-.836 1.338-1.362 0-1.426-2.086-3.18-4.676-3.18-.427 0-.888.088-1.301.197z"
                            fill="black"
                        />
                    </Svg>
                </View>
                <View>
                    <Text>Gymondo GmbH</Text>
                    <Text>Ritterstraße 12-14</Text>
                    <Text>10969 Berlin</Text>
                    <Text>
                        <Text style={styles.boldText}>E-Mail: </Text>
                        service@gymondo.de
                    </Text>
                    <Text>
                        <Text style={styles.boldText}>Web: </Text>
                        www.gymondo.com
                    </Text>
                    <Text style={styles.alignRight}>Datum {getLocalDate()}</Text>
                </View>
                <View>
                    <Text>
                        {account.firstName} {account.lastName}
                    </Text>
                    <Text>
                        {account.address?.street} {account.address?.number}
                    </Text>
                    <Text>
                        {account.address?.location?.postalCode} {account.address?.location?.location}
                    </Text>
                    <Text> E-Mail-Adresse: {account.username}</Text>
                </View>
                <View>
                    <Text style={[styles.head, styles.boldText]}>Zahlungsbestätigung</Text>
                </View>
                <View>
                    <Text>
                        {account.firstName} {account.lastName} hat <Text>{item.title}</Text> für {item.price}&nbsp;
                        {item.currency.symbol} (inkl. 19% Mwst.) am {getLocalDate(item.purchaseDate)} gekauft.
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export default PaymentConfirmationItem;
